import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Input from "../../../components/common/Input";
import Select from "../../../components/common/Select";
import Button from "../../../components/common/Button";
import { createBranchThunk } from "../../../store/thunks/branchThunks";
import { getCompanyDetailsThunk } from "../../../store/thunks/companyDetailsThunks";
import { LocationMarkerIcon, ShoppingBagIcon } from "@heroicons/react/solid";

const CreateBranchStep = ({ onComplete }) => {
  const dispatch = useDispatch();
  const { companyDetails } = useSelector((state) => state.companyDetails);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [isPrePopulated, setIsPrePopulated] = useState(false);

  // Initialize with empty form data
  const [formData, setFormData] = useState({
    name: "",
    code: "",
    branchType: "STANDARD",
    address: {
      line1: "",
      line2: "",
      city: "",
      state: "",
      pinCode: "",
      landmark: "",
    },
    contact: {
      phoneNumber: "",
      alternatePhone: "",
      email: "",
      managerName: "",
      managerPhone: "",
    },
    cityTier: "TIER2",
    rateConfig: {
      ratePolicy: "INHERIT",
      rateMode: "ADMIN",
    },
    settings: {
      allowCrossBranchRedemption: false,
      allowInterBranchTransfer: true,
      requiresApprovalForHighValue: true,
      highValueThreshold: 50000,
      maxDiscountPercent: 10,
    },
    compliance: {
      gstNumber: "",
      tradeLicense: "",
    },
  });

  // Fetch company details if not already loaded
  useEffect(() => {
    if (!companyDetails) {
      dispatch(getCompanyDetailsThunk());
    }
  }, [dispatch, companyDetails]);

  // Pre-populate form with company details
  useEffect(() => {
    if (companyDetails && !isPrePopulated) {
      setFormData((prevData) => ({
        ...prevData,
        name: `${companyDetails.companyName} - Main Branch`,
        address: {
          line1: companyDetails.address?.line1 || "",
          line2: companyDetails.address?.line2 || "",
          city: companyDetails.address?.city || "",
          state: mapStateToCode(companyDetails.address?.state) || "",
          pinCode: companyDetails.address?.pinCode || "",
          landmark: companyDetails.address?.landmark || "",
        },
        contact: {
          phoneNumber: companyDetails.contactPerson?.phone || "",
          alternatePhone: "",
          email: companyDetails.contactPerson?.email || "",
          managerName: companyDetails.contactPerson?.name || "",
          managerPhone: companyDetails.contactPerson?.phone || "",
        },
        compliance: {
          gstNumber: companyDetails.taxId || "",
          tradeLicense: companyDetails.registrationNumber || "",
        },
        // Set city tier based on city (you can enhance this logic)
        cityTier: determineCityTier(companyDetails.address?.city),
      }));
      setIsPrePopulated(true);
    }
  }, [companyDetails, isPrePopulated]);

  // Helper function to map state names to codes
  const mapStateToCode = (stateName) => {
    const stateMap = {
      Maharashtra: "MH",
      Gujarat: "GJ",
      Rajasthan: "RJ",
      Delhi: "DL",
      Karnataka: "KA",
      "Tamil Nadu": "TN",
      "Uttar Pradesh": "UP",
      "West Bengal": "WB",
      "Andhra Pradesh": "AP",
      Telangana: "TG",
    };
    return stateMap[stateName] || "";
  };

  // Helper function to determine city tier
  const determineCityTier = (city) => {
    const metroCities = [
      "Mumbai",
      "Delhi",
      "Bangalore",
      "Chennai",
      "Kolkata",
      "Hyderabad",
      "Pune",
      "Ahmedabad",
    ];
    const tier1Cities = [
      "Surat",
      "Jaipur",
      "Lucknow",
      "Kanpur",
      "Nagpur",
      "Indore",
      "Bhopal",
      "Visakhapatnam",
    ];

    if (metroCities.includes(city)) return "METRO";
    if (tier1Cities.includes(city)) return "TIER1";
    return "TIER2";
  };

  const stateOptions = [
    { value: "MH", label: "Maharashtra" },
    { value: "GJ", label: "Gujarat" },
    { value: "RJ", label: "Rajasthan" },
    { value: "DL", label: "Delhi" },
    { value: "KA", label: "Karnataka" },
    { value: "TN", label: "Tamil Nadu" },
    { value: "UP", label: "Uttar Pradesh" },
    { value: "WB", label: "West Bengal" },
    { value: "AP", label: "Andhra Pradesh" },
    { value: "TG", label: "Telangana" },
  ];

  const branchTypeOptions = [
    { value: "STANDARD", label: "Standard Branch" },
    { value: "FLAGSHIP", label: "Flagship Store" },
    { value: "FRANCHISE", label: "Franchise" },
    { value: "WAREHOUSE", label: "Warehouse" },
  ];

  const cityTierOptions = [
    { value: "METRO", label: "Metro City" },
    { value: "TIER1", label: "Tier 1 City" },
    { value: "TIER2", label: "Tier 2 City" },
    { value: "TIER3", label: "Tier 3 City" },
    { value: "RURAL", label: "Rural Area" },
  ];

  const ratePolicyOptions = [
    { value: "INHERIT", label: "Inherit from Main Branch" },
    { value: "CUSTOM", label: "Custom Rates" },
    { value: "HYBRID", label: "Hybrid (Mix of Both)" },
  ];

  const handleChange = (field, value) => {
    if (field.includes(".")) {
      const [parent, child] = field.split(".");
      setFormData({
        ...formData,
        [parent]: {
          ...formData[parent],
          [child]: value,
        },
      });
    } else {
      setFormData({
        ...formData,
        [field]: value,
      });
    }

    // Clear error for this field
    if (errors[field]) {
      setErrors({ ...errors, [field]: "" });
    }
  };

  const validate = () => {
    const newErrors = {};

    // Required fields
    if (!formData.name) newErrors.name = "Branch name is required";
    if (!formData.address.line1)
      newErrors["address.line1"] = "Address is required";
    if (!formData.address.city) newErrors["address.city"] = "City is required";
    if (!formData.address.state)
      newErrors["address.state"] = "State is required";
    if (!formData.address.pinCode)
      newErrors["address.pinCode"] = "PIN code is required";
    if (!formData.contact.phoneNumber)
      newErrors["contact.phoneNumber"] = "Phone number is required";

    // Validate PIN code format
    if (
      formData.address.pinCode &&
      !/^[1-9][0-9]{5}$/.test(formData.address.pinCode)
    ) {
      newErrors["address.pinCode"] = "Invalid PIN code format";
    }

    // Validate phone number format
    if (
      formData.contact.phoneNumber &&
      !/^[6-9]\d{9}$/.test(formData.contact.phoneNumber)
    ) {
      newErrors["contact.phoneNumber"] = "Invalid phone number format";
    }

    // Validate GST number if provided
    if (
      formData.compliance.gstNumber &&
      !/^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/.test(
        formData.compliance.gstNumber
      )
    ) {
      newErrors["compliance.gstNumber"] = "Invalid GST number format";
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }

    setLoading(true);
    try {
      // Generate branch code if not provided
      if (!formData.code) {
        const stateCode = formData.address.state;
        const cityCode = formData.address.city.substring(0, 2).toUpperCase();
        const random = Math.random().toString(36).substring(2, 4).toUpperCase();
        formData.code = `BR-${stateCode}-${cityCode}-${random}`;
      }

      // Mark this as the main branch for first-time setup
      const branchData = {
        ...formData,
        isMainBranch: true,
        customerId:
          companyDetails?.customerId || localStorage.getItem("customerId"),
        ownerId: companyDetails?.ownerId || localStorage.getItem("ownerId"),
      };

      const result = await dispatch(createBranchThunk(branchData)).unwrap();

      onComplete({
        branchCreated: true,
        branchId: result._id,
        branchCode: result.code,
      });
    } catch (error) {
      setErrors({
        general: error || "Failed to create branch",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSkip = () => {
    onComplete({ skipped: true });
  };

  const handleResetToCompanyDetails = () => {
    if (companyDetails) {
      setFormData((prevData) => ({
        ...prevData,
        address: {
          line1: companyDetails.address?.line1 || "",
          line2: companyDetails.address?.line2 || "",
          city: companyDetails.address?.city || "",
          state: mapStateToCode(companyDetails.address?.state) || "",
          pinCode: companyDetails.address?.pinCode || "",
          landmark: companyDetails.address?.landmark || "",
        },
        contact: {
          ...prevData.contact,
          phoneNumber: companyDetails.contactPerson?.phone || "",
          email: companyDetails.contactPerson?.email || "",
          managerName: companyDetails.contactPerson?.name || "",
          managerPhone: companyDetails.contactPerson?.phone || "",
        },
        compliance: {
          gstNumber: companyDetails.taxId || "",
          tradeLicense: companyDetails.registrationNumber || "",
        },
      }));
    }
  };

  return (
    <div>
      <div className="mb-6">
        <h3 className="text-lg font-semibold mb-2">Create Your Main Branch</h3>
        <p className="text-gray-600">
          We've pre-filled some details from your company information. Please
          review and update as needed.
        </p>
        {companyDetails && (
          <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-sm text-blue-800">
              <strong>Tip:</strong> This will be your main branch. The details
              have been pre-populated from your company information.
            </p>
            <button
              type="button"
              onClick={handleResetToCompanyDetails}
              className="text-sm text-blue-600 hover:text-blue-700 mt-1 underline"
            >
              Reset to company details
            </button>
          </div>
        )}
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-4">
          {errors.general}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Basic Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center">
            <ShoppingBagIcon className="w-5 h-5 mr-2" />
            Basic Information
          </h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Branch Name"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              error={errors.name}
              placeholder="e.g., Main Branch Mumbai"
              required
            />
            <Input
              label="Branch Code (Optional)"
              value={formData.code}
              onChange={(e) => handleChange("code", e.target.value)}
              placeholder="Auto-generated if empty"
            />
            <Select
              label="Branch Type"
              value={formData.branchType}
              onChange={(e) => handleChange("branchType", e.target.value)}
              options={branchTypeOptions}
            />
            <Select
              label="City Tier"
              value={formData.cityTier}
              onChange={(e) => handleChange("cityTier", e.target.value)}
              options={cityTierOptions}
            />
          </div>
        </div>

        {/* Rest of the form remains the same... */}
        {/* Address Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4 flex items-center">
            <LocationMarkerIcon className="w-5 h-5 mr-2" />
            Address Details
          </h4>
          <div className="space-y-4">
            <Input
              label="Address Line 1"
              value={formData.address.line1}
              onChange={(e) => handleChange("address.line1", e.target.value)}
              error={errors["address.line1"]}
              placeholder="Building/Street address"
              required
            />
            <Input
              label="Address Line 2"
              value={formData.address.line2}
              onChange={(e) => handleChange("address.line2", e.target.value)}
              placeholder="Area/Locality (Optional)"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="City"
                value={formData.address.city}
                onChange={(e) => handleChange("address.city", e.target.value)}
                error={errors["address.city"]}
                placeholder="City name"
                required
              />
              <Select
                label="State"
                value={formData.address.state}
                onChange={(e) => handleChange("address.state", e.target.value)}
                options={stateOptions}
                error={errors["address.state"]}
                required
              />
              <Input
                label="PIN Code"
                value={formData.address.pinCode}
                onChange={(e) =>
                  handleChange("address.pinCode", e.target.value)
                }
                error={errors["address.pinCode"]}
                placeholder="6-digit PIN"
                maxLength="6"
                required
              />
            </div>
            <Input
              label="Landmark"
              value={formData.address.landmark}
              onChange={(e) => handleChange("address.landmark", e.target.value)}
              placeholder="Near landmark (Optional)"
            />
          </div>
        </div>

        {/* Contact Information */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Contact Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="Primary Phone"
              value={formData.contact.phoneNumber}
              onChange={(e) =>
                handleChange("contact.phoneNumber", e.target.value)
              }
              error={errors["contact.phoneNumber"]}
              placeholder="10-digit mobile number"
              maxLength="10"
              required
            />
            <Input
              label="Alternate Phone"
              value={formData.contact.alternatePhone}
              onChange={(e) =>
                handleChange("contact.alternatePhone", e.target.value)
              }
              placeholder="Optional"
              maxLength="10"
            />
            <Input
              label="Email"
              type="email"
              value={formData.contact.email}
              onChange={(e) => handleChange("contact.email", e.target.value)}
              placeholder="branch@example.com"
            />
            <Input
              label="Manager Name"
              value={formData.contact.managerName}
              onChange={(e) =>
                handleChange("contact.managerName", e.target.value)
              }
              placeholder="Branch manager name"
            />
          </div>
        </div>

        {/* Rate Configuration */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Rate Configuration</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Select
              label="Rate Policy"
              value={formData.rateConfig.ratePolicy}
              onChange={(e) =>
                handleChange("rateConfig.ratePolicy", e.target.value)
              }
              options={ratePolicyOptions}
            />
            <Select
              label="Rate Mode"
              value={formData.rateConfig.rateMode}
              onChange={(e) =>
                handleChange("rateConfig.rateMode", e.target.value)
              }
              options={[
                { value: "ADMIN", label: "Admin Controlled" },
                { value: "LIVE", label: "Live Market Rates" },
                { value: "HYBRID", label: "Hybrid Mode" },
              ]}
            />
          </div>
        </div>

        {/* Compliance (Optional) */}
        <div className="bg-gray-50 rounded-lg p-4">
          <h4 className="font-medium mb-4">Compliance Information</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Input
              label="GST Number"
              value={formData.compliance.gstNumber}
              onChange={(e) =>
                handleChange("compliance.gstNumber", e.target.value)
              }
              error={errors["compliance.gstNumber"]}
              placeholder="15-character GSTIN"
              maxLength="15"
            />
            <Input
              label="Trade License"
              value={formData.compliance.tradeLicense}
              onChange={(e) =>
                handleChange("compliance.tradeLicense", e.target.value)
              }
              placeholder="License number"
            />
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-between">
          <Button
            type="button"
            variant="secondary-outline"
            onClick={handleSkip}
          >
            Skip for Now
          </Button>
          <Button
            type="submit"
            variant="primary"
            isLoading={loading}
            disabled={loading}
          >
            Create Branch & Continue
          </Button>
        </div>
      </form>
    </div>
  );
};

export default CreateBranchStep;
